import React from 'react'
import { FieldValues, useController, UseControllerProps } from 'react-hook-form'

import ErrorMessage from '@/components/formik/messages/ErrorMessage'

import TextInput, { TextInputProps } from './TextInput'

type NumberInputControllerProps<T extends FieldValues> = UseControllerProps<T> & Omit<TextInputProps, 'onBlur' | 'onChange' | 'value' | 'type'>

/**
 * Numeric variant of TextInputController: HTML number inputs emit strings, so this converts on
 * change and stores real numbers in the form state (an empty field becomes undefined). Keeps
 * zod schemas on plain z.number() — no coercion, so input and output types stay identical.
 */
export default function NumberInputController<T extends FieldValues>(props: NumberInputControllerProps<T>) {
  const { control, helperText, name, rules, InputProps, ...rest } = props
  const {
    field,
    fieldState: { error },
  } = useController({ name, control, rules })

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value
    field.onChange(raw === '' ? undefined : Number(raw))
  }

  return (
    <TextInput
      {...rest}
      type="number"
      error={!!error}
      helperText={helperText ? helperText : error?.message ? <ErrorMessage error={error.message} /> : undefined}
      inputRef={field.ref}
      InputProps={{
        onBlur: field.onBlur,
        ...InputProps,
      }}
      name={field.name}
      onChange={handleChange}
      value={field.value ?? ''}
    />
  )
}
